import { useCallback, useEffect, useRef, useState } from 'react';
import dayjs, { UnitType } from 'dayjs';
import duration, { Duration } from 'dayjs/plugin/duration';

dayjs.extend(duration);

const SECOND = 1000;
const MILLISECONDS: UnitType = 'millisecond';
const DEFAULT_TIME_FORMATS: UnitType[] = ['hour', 'minute', 'second'];

type TimeFormats = Record<UnitType, number>;
type Countdown = {
  timeFormats: TimeFormats;
  isTimeElapsed: boolean;
  updateCountdown: (v: number) => void;
  getCountdownMs: () => number;
};

interface Props {
  duration: number; // Duration for timer in ms
  timeFormats?: UnitType[];
}

export const useCountdown = ({ duration, timeFormats = DEFAULT_TIME_FORMATS }: Props): Countdown => {
  const countdown = useRef<Duration>(dayjs.duration(duration, MILLISECONDS));
  const [countdownFormats, setCountdownFormats] = useState<TimeFormats>({});
  const [updateTimerInterval, setUpdateTimerInterval] = useState<number>();
  const [isTimeElapsed, setIsTimeElapsed] = useState<boolean>(false);

  const updateTimeFormats = useCallback(() => {
    setCountdownFormats(
      timeFormats.reduce(
        (acc: TimeFormats, curr: UnitType): TimeFormats => ({
          ...acc,
          [curr]: countdown.current.get(curr)
        }),
        {} as TimeFormats
      )
    );
  }, [setCountdownFormats, countdown, timeFormats]);

  useEffect(() => {
    const updateTimer = setInterval(() => {
      updateTimeFormats();
      setIsTimeElapsed(countdown.current?.asMilliseconds() <= 0);
      countdown.current = countdown.current?.subtract(SECOND, MILLISECONDS);
    }, SECOND);

    setUpdateTimerInterval(updateTimer);

    return () => {
      clearInterval(updateTimer);
    };
  }, [updateTimeFormats]);

  useEffect(() => {
    if (isTimeElapsed) {
      clearInterval(updateTimerInterval);
    }
  }, [isTimeElapsed, updateTimerInterval]);

  const updateCountdown = useCallback((sub: number) => {
    const update = Math.min(sub, countdown.current?.asMilliseconds() || 0);

    countdown.current = countdown.current?.subtract(update, MILLISECONDS);
  }, []);

  const getCountdownMs = useCallback(() => countdown.current?.asMilliseconds() || 0, []);

  return {
    timeFormats: { ...countdownFormats },
    isTimeElapsed,
    updateCountdown,
    getCountdownMs
  };
};
